import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Avatar,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  AccessTime,
  CheckCircle,
  Warning,
  Code,
  Analytics,
  Security,
  VideoLibrary,
  NavigateNext,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import AnswersDisplay from '../components/results/AnswersDisplay';
import VideoReviewPlayer from '../components/results/VideoReviewPlayer';
// Typing analytics deprecated in favor of video-centric approach
import SecurityFlags from '../components/results/SecurityFlags';
import { resultsAPI, InterviewResults, infractionsAPI, Infraction, questionAPI, Question } from '../services/apiService';

// Use the InterviewResults type from apiService

interface Answer {
  uuid: string;
  questionId: string;
  questionContent: string;
  content: string;
  recorder: string;
  frames: string;
  duration: number;
  language: string;
  metadata?: {
    timestamp: string;
    suspiciousEvents?: string[];
    [key: string]: any;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`results-tabpanel-${index}`}
      aria-labelledby={`results-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const InterviewResultsPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { setError } = useAuth();
  
  const [interviewResult, setInterviewResult] = useState<InterviewResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setLocalError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [infractions, setInfractions] = useState<Infraction[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);

  // Calculate interview duration from timestamps (more reliable than backend duration field)
  const calculateInterviewDuration = (startedAt: string, completedAt: string): number => {
    try {
      const start = new Date(startedAt).getTime();
      const end = new Date(completedAt).getTime();
      
      if (isNaN(start) || isNaN(end)) {
        console.warn('[InterviewResults] Invalid timestamps for duration calculation:', { startedAt, completedAt });
        return 0;
      }
      
      const durationMs = end - start;
      const durationSeconds = Math.floor(durationMs / 1000);
      
      logger.debug('InterviewResults: Calculated duration', {
        startedAt,
        completedAt, 
        durationMs,
        durationSeconds,
        formatted: `${Math.floor(durationSeconds / 60)}:${(durationSeconds % 60).toString().padStart(2, '0')}`
      });
      
      return Math.max(0, durationSeconds); // Ensure non-negative
    } catch (error) {
      console.error('[InterviewResults] Error calculating duration:', error);
      return 0;
    }
  };
  const [answersWithQuestions, setAnswersWithQuestions] = useState<Answer[]>([]);

  // Load infractions from API
  const loadInfractions = async (interviewId: string) => {
    try {
      logger.debug('Loading infractions for interview', { interviewId });
      const infractionResults = await infractionsAPI.getInfractions(interviewId);
      setInfractions(infractionResults);
      logger.debug('Loaded infractions', { count: infractionResults.length });
    } catch (error) {
      console.error('Failed to load infractions:', error);
      setInfractions([]); // Set empty array on error
    }
  };

  // Merge questions with answers
  const mergeQuestionsWithAnswers = (answers: any[], questions: Question[]): Answer[] => {
    return answers.map((answer, index) => {
      const question = questions.find(q => q.id === answer.question_id) || questions[index];
      return {
        ...answer,
        questionId: answer.question_id,
        questionContent: question?.content || `Question ${index + 1} content`,
        language: answer.metadata?.language || 'text',
      };
    });
  };

  // Load interview results
  useEffect(() => {
    const loadInterviewResults = async () => {
      if (!uuid) {
        setLocalError('Interview UUID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setLocalError(null);
        
        const response = await resultsAPI.getInterviewResults(uuid);
        setInterviewResult(response);
        
        // Load questions for the test
        const questionsData = await questionAPI.getByTestUuid(response.test.uuid);
        setQuestions(questionsData);
        
        // Merge questions with answers
        const mergedAnswers = mergeQuestionsWithAnswers(response.answers, questionsData);
        setAnswersWithQuestions(mergedAnswers);
        
        // Also load infractions and violation evidence for this interview
        await loadInfractions(uuid);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load interview results';
        setLocalError(errorMessage);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadInterviewResults();
  }, [uuid]); // Removed setError dependency as it's not used in the effect

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBackClick = () => {
    // Navigate back to test details page
    if (interviewResult) {
      navigate(`/tests/${interviewResult.test.uuid}`);
    } else {
      navigate('/tests');
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'abandoned':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle />;
      case 'in_progress':
        return <AccessTime />;
      default:
        return <Warning />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !interviewResult) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
          sx={{ mb: 3 }}
        >
          Back to Test
        </Button>
        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
          {error || 'Interview results not found'}
        </Alert>
      </Container>
    );
  }

  const currentAnswer = interviewResult.answers[selectedQuestionIndex];
  const suspiciousEventsCount = interviewResult.answers.reduce(
    (count, answer) => count + (answer.metadata?.suspiciousEvents?.length || 0), 0
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button & Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
          sx={{ mb: 2 }}
        >
          Back to Test
        </Button>
        
        <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
          <Link
            color="inherit"
            onClick={() => navigate('/tests')}
            sx={{ cursor: 'pointer' }}
          >
            Tests
          </Link>
          <Link
            color="inherit"
            onClick={handleBackClick}
            sx={{ cursor: 'pointer' }}
          >
            {interviewResult.test.title}
          </Link>
          <Typography color="text.primary">
            Interview Results
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Interview Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}>
              <Person fontSize="large" />
            </Avatar>
          </Grid>
          
          <Grid item xs>
            <Typography variant="h5" gutterBottom>
              {interviewResult.interview.candidateName}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {interviewResult.interview.candidateEmail}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {interviewResult.test.title}
            </Typography>
          </Grid>

          <Grid item>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
              <Chip
                icon={getStatusIcon(interviewResult.interview.status)}
                label={interviewResult.interview.status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(interviewResult.interview.status)}
                size="small"
              />
              
              {interviewResult.interview.completedAt && (
                <Typography variant="body2" color="text.secondary">
                  Completed: {new Date(interviewResult.interview.completedAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

      </Paper>

      {/* Tabs */}
      <Paper elevation={1}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<Code />} 
            label="Questions & Answers" 
            iconPosition="start"
          />
          <Tab 
            icon={<Security />} 
            label="Security Review" 
            iconPosition="start"
            sx={suspiciousEventsCount > 0 ? { color: 'error.main' } : {}}
          />
        </Tabs>

        {/* Tab Content */}
        <TabPanel value={activeTab} index={0}>
          <AnswersDisplay
            answers={answersWithQuestions.length > 0 ? answersWithQuestions : interviewResult.answers}
            selectedQuestionIndex={selectedQuestionIndex}
            onQuestionSelect={setSelectedQuestionIndex}
          />
        </TabPanel>

        {/* <TabPanel value={activeTab} index={1}>
          <AnalyticsDashboard
            answers={interviewResult.answers}
            overallAnalytics={interviewResult.analytics}
          />
        </TabPanel> */}

        <TabPanel value={activeTab} index={1}>
          <SecurityFlags
            answers={interviewResult.answers}
            interviewId={uuid!}
            infractions={infractions}
            candidateName={interviewResult.interview.candidateName}
            duration={calculateInterviewDuration(interviewResult.interview.startedAt, interviewResult.interview.completedAt)}
            onJumpToReplay={(questionIndex: number) => {
              setSelectedQuestionIndex(questionIndex);
              setActiveTab(0); // Jump to Questions & Answers tab
            }}
          />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default InterviewResultsPage;